import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';

import PageWrapper from 'commons/src/components/containers/PageWrapper';
import WhiteSectionContainer from 'commons/src/components/containers/WhiteSectionContainer';
import SectionHeader from 'commons/src/components/headers/SectionHeader';
import { mediumFormLoader } from 'commons/src/components/placeholder';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import { ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { toErrorType } from 'commons/src/sagas/isErrorType';
import { useGetThresholdsQuery } from '../../../api';
import { Store } from '../../../reducers';
import { locationCustomThresholdSelector } from './AccountThresholdSelector';
import styles from './AccountThresholdsPage.module.scss';
import CustomThresholds from './CustomThresholdSection';
import ThresholdMgmt from './ThresholdMgmt';

export const AccountThresholdsPage = (): React.ReactElement => {
    const { t: txt } = useTranslation();
    const { selectedGroup } = useSelector((state: Store) => state.userSettings);
    const { loading: locationsLoading, locations } = useSelector((state: Store) => state.locations);
    const {
        data = {},
        error,
        isSuccess,
    } = useGetThresholdsQuery(
        selectedGroup?.id ? { resourceId: selectedGroup.id, scope: ThresholdsResourceScope.USER_GROUP } : skipToken
    );

    const fetchThresholdsError = error ? toErrorType(error) : undefined;
    const customThresholds = useSelector((state: Store) => locationCustomThresholdSelector(state));
    const loadingState = (!isSuccess && Object.keys(data).length === 0) || (locationsLoading && locations.length === 0);

    return (
        <PageWrapper pageType="medium">
            <SectionHeader headerName="CustomThresholds.SensorThresholds" headerType={3} initialHeader />
            <p className={styles.p}>{txt('CustomThresholds.AccountDescription')}</p>
            <WhiteSectionContainer>
                {fetchThresholdsError ? (
                    <ResponseBox text={fetchThresholdsError.error} />
                ) : (
                    <ReactPlaceholder ready={!loadingState} customPlaceholder={mediumFormLoader}>
                        <CustomThresholds
                            childResourceScope={ThresholdsResourceScope.LOCATION}
                            headerTextKey="CustomThresholds.AccountLocationDescription"
                            customThresholds={customThresholds}
                        />
                        <ThresholdMgmt
                            thresholdScope={ThresholdsResourceScope.USER_GROUP}
                            thresholdData={data}
                            buttonText="ThresholdSettings.UpdateAccountThresholds"
                            id={selectedGroup?.id}
                        />
                    </ReactPlaceholder>
                )}
            </WhiteSectionContainer>
        </PageWrapper>
    );
};

export default AccountThresholdsPage;
